import React from "react";
import { tv, type VariantProps } from "tailwind-variants";

import { twColorMap } from "@/components/ui/utils";

const avatarStyles = tv({
  base: ["relative flex shrink-0 overflow-hidden rounded-full"],
  variants: {
    size: {
      small: "size-6",
      medium: "size-7",
      large: "size-8",
    },
    color: {
      ...twColorMap,
    },
  },

  defaultVariants: {
    size: "medium",
  },
});

type AvatarProps = Omit<React.ComponentProps<"span">, "color"> &
  VariantProps<typeof avatarStyles> & {
    src?: string | null;
    initials?: string;
    alt?: string;
    className?: string;
    role?: string;
    fallback?: React.ReactNode;
  };

export function Avatar({
  ref,
  src = null,
  initials,
  alt = "",
  children,
  className,
  size,
  color,
  role = "avatar",
  fallback,
  ...props
}: AvatarProps) {
  const badgeId = React.useId();
  const ariaLabelledby = [badgeId, children ? badgeId : ""].join(" ");
  return (
    <span
      ref={ref}
      aria-labelledby={ariaLabelledby}
      role={role}
      data-slot="avatar"
      {...props}
      className={avatarStyles({ size, color, className })}
    >
      {initials ? (
        <svg
          className="select-none fill-current text-4xl uppercase font-medium text-foreground"
          viewBox="0 0 100 100"
          aria-hidden={alt ? undefined : "true"}
        >
          {alt && <title>{alt}</title>}
          <text
            x="50%"
            y="50%"
            alignmentBaseline="middle"
            dominantBaseline="middle"
            textAnchor="middle"
            dy=".125em"
          >
            {initials}
          </text>
        </svg>
      ) : fallback ? (
        <div className="flex items-center justify-center">{fallback}</div>
      ) : null}
      {src && <img src={src} alt={alt} />}
    </span>
  );
}

export function getAvatarFallback(name?: string | null): string | undefined {
  const [first, last] = name?.split(" ") || [];
  const initials = `${first?.[0] || ""}${last?.[0] || ""}`;
  if (initials) {
    return initials.toUpperCase();
  }
  return undefined;
}
